import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronRight, 
  Star,
  Clock,
  Activity,
  Smartphone,
  TabletSmartphone,
  Shield,
  Settings,
  Target,
  Download,
  Users,
  CheckCircle,
  MessageSquare,
  BarChart,
  Layers,
  UserCheck,
  Truck,
  Ambulance,
  Car
} from 'lucide-react';
import { translations } from '../translations';
import TrackingAnimation from '../components/TrackingAnimation';
import PricingTable from '../components/PricingTable';
import WaitlistSection from '../components/WaitlistSection';
import DriviLogo from '../assets/logo.svg';
import ContactForm from '../components/ContactForm';
import MobileAppSection from '../components/MobileAppSection';

const Home = ({ language = 'en' }) => {
  const t = translations[language];
  const mobileAppRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const location = useLocation();

  useEffect(() => {
    // Handle scrolling to section after navigation
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        setTimeout(() => {
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100); // Small delay to ensure the page is rendered
      }
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-up, .stagger-children');
      elements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight * 0.8;
        if (isVisible) {
          element.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (mobileAppRef.current) {
        const rect = mobileAppRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setMousePosition({ x: x - 0.5, y: y - 0.5 });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <TrackingAnimation language={language} />
        </div>
        
        {/* Content Overlay */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2c3e50] mb-6 md:mb-8 tracking-tight">
            {t.hero.title}
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 md:mb-12 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 md:mb-16">
            <Link
              to="/coming-soon"
              className="w-full sm:w-auto bg-[#3498DB] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-[#2980B9] transition duration-300 shadow-lg hover:shadow-xl flex items-center justify-center hover-lift"
            >
              {t.hero.cta.demo}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/coming-soon"
              className="w-full sm:w-auto bg-white border-2 border-[#3498DB] text-[#3498DB] px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-blue-50 transition duration-300 flex items-center justify-center hover-lift"
            >
              {t.hero.cta.plans}
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-3xl sm:text-4xl font-bold text-[#2c3e50] mb-2">{t.hero.stats.uptime.value}</div>
              <div className="text-gray-600 text-sm sm:text-base">{t.hero.stats.uptime.label}</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-3xl sm:text-4xl font-bold text-[#2c3e50] mb-2">{t.hero.stats.clients.value}</div>
              <div className="text-gray-600 text-sm sm:text-base">{t.hero.stats.clients.label}</div>
            </div>
            <div className="sm:col-span-2 md:col-span-1 bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="text-3xl sm:text-4xl font-bold text-[#2c3e50] mb-2">{t.hero.stats.vehicles.value}</div>
              <div className="text-gray-600 text-sm sm:text-base">{t.hero.stats.vehicles.label}</div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden z-20">
          <svg
            viewBox="0 0 1440 120"
            className="relative w-full h-[40px] sm:h-[60px]"
            preserveAspectRatio="none"
          >
            <path
              d="M0,32L60,42.7C120,53,240,75,360,80C480,85,600,75,720,58.7C840,43,960,21,1080,16C1200,11,1320,21,1380,26.7L1440,32L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"
              fill="#ffffff"
              fillOpacity="1"
            />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.features.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.features.items.map((feature, index) => (
              <div key={index} className="relative group p-8 rounded-2xl border border-gray-200 hover:border-[#3498DB] transition-all duration-300 hover:shadow-xl hover-lift">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-6">
                  {index === 0 && <BarChart className="h-6 w-6 text-[#3498DB]" />}
                  {index === 1 && <Layers className="h-6 w-6 text-[#3498DB]" />}
                  {index === 2 && <Shield className="h-6 w-6 text-[#3498DB]" />}
                  {index === 3 && <Activity className="h-6 w-6 text-[#3498DB]" />}
                  {index === 4 && <UserCheck className="h-6 w-6 text-[#3498DB]" />}
                  {index === 5 && <Clock className="h-6 w-6 text-[#3498DB]" />}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Us Section */}
      <section id="trust" className="py-20 bg-gradient-to-br from-[#3498DB]/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#2c3e50] mb-4">{t.trust.title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.trust.subtitle}
            </p>
          </div>

          {/* Featured Partners - Grid Layout */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* City Express */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-[#3498DB] transition-all duration-300 hover:shadow-xl group">
              <div className="flex items-center justify-between mb-6">
                <div className="text-2xl font-bold text-gray-400 group-hover:text-[#3498DB] transition-colors">
                  {t.trust.partners.cityExpress.name}
                </div>
                <div className="px-4 py-2 bg-[#3498DB]/10 rounded-full">
                  <span className="text-sm font-medium text-[#3498DB]">Local Partner</span>
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg">
                "{t.trust.partners.cityExpress.testimonial}"
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-[#3498DB]/10 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#3498DB]" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{t.trust.partners.cityExpress.type}</div>
                    <div className="text-sm text-gray-500">{t.trust.partners.cityExpress.vehicles}</div>
                  </div>
                </div>
                <div className="flex text-[#3498DB]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
              </div>
            </div>

            {/* Green Logistics */}
            <div className="bg-[#2c3e50] p-8 rounded-2xl hover:shadow-xl transition-all duration-300 transform group">
              <div className="flex items-center justify-between mb-6">
                <div className="text-2xl font-bold text-white group-hover:text-[#3498DB] transition-colors">
                  {t.trust.partners.greenLogistics.name}
                </div>
                <div className="px-4 py-2 bg-white/10 rounded-full">
                  <span className="text-sm font-medium text-white">Regional Partner</span>
                </div>
              </div>
              <p className="text-white/90 mb-6 text-lg">
                "{t.trust.partners.greenLogistics.testimonial}"
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white">{t.trust.partners.greenLogistics.type}</div>
                    <div className="text-sm text-white/70">{t.trust.partners.greenLogistics.vehicles}</div>
                  </div>
                </div>
                <div className="flex text-[#3498DB]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Partners - Compact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Courier */}
            <div className="relative overflow-hidden bg-white rounded-xl p-6 border border-gray-100 hover:border-[#3498DB] transition-all duration-300 group">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-[#3498DB]" />
                </div>
                <div className="flex-1">
                  <div className="text-xl font-bold text-gray-400 group-hover:text-[#3498DB] transition-colors mb-2">
                    {t.trust.partners.quickCourier.name}
                  </div>
                  <div className="text-sm text-gray-500 mb-2">{t.trust.partners.quickCourier.type}</div>
                  <p className="text-gray-600 text-sm">{t.trust.partners.quickCourier.testimonial}</p>
                  <div className="mt-4 text-[#3498DB] text-sm font-medium">{t.trust.partners.quickCourier.vehicles}</div>
                </div>
              </div>
            </div>

            {/* Metro Taxi */}
            <div className="relative overflow-hidden bg-[#3498DB]/5 rounded-xl p-6 hover:bg-[#3498DB]/10 transition-all duration-300 group">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#3498DB]/20 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-[#3498DB]" />
                </div>
                <div className="flex-1">
                  <div className="text-xl font-bold text-[#2c3e50] group-hover:text-[#3498DB] transition-colors mb-2">
                    {t.trust.partners.taxiFleet.name}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">{t.trust.partners.taxiFleet.type}</div>
                  <p className="text-gray-600 text-sm">{t.trust.partners.taxiFleet.testimonial}</p>
                  <div className="mt-4 text-[#3498DB] text-sm font-medium">{t.trust.partners.taxiFleet.vehicles}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Success Metrics */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="relative overflow-hidden rounded-lg p-6 bg-gradient-to-br from-[#3498DB] to-[#2980B9] text-white transform hover:scale-105 transition-all duration-300">
              <div className="text-4xl font-bold mb-2">{t.trust.metrics.activeFleets.value}</div>
              <div className="text-white/90">{t.trust.metrics.activeFleets.label}</div>
            </div>
            <div className="relative overflow-hidden rounded-lg p-6 bg-white border border-gray-100 transform hover:scale-105 transition-all duration-300">
              <div className="text-4xl font-bold text-[#3498DB] mb-2">{t.trust.metrics.vehiclesTracked.value}</div>
              <div className="text-gray-600">{t.trust.metrics.vehiclesTracked.label}</div>
            </div>
            <div className="relative overflow-hidden rounded-lg p-6 bg-[#2c3e50] text-white transform hover:scale-105 transition-all duration-300">
              <div className="text-4xl font-bold mb-2">{t.trust.metrics.satisfaction.value}</div>
              <div className="text-white/90">{t.trust.metrics.satisfaction.label}</div>
            </div>
            <div className="relative overflow-hidden rounded-lg p-6 bg-gradient-to-br from-[#3498DB]/10 to-white border border-gray-100 transform hover:scale-105 transition-all duration-300">
              <div className="text-4xl font-bold text-[#2c3e50] mb-2">{t.trust.metrics.costReduction.value}</div>
              <div className="text-gray-600">{t.trust.metrics.costReduction.label}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <MobileAppSection language={language} t={t} />

      {/* Waitlist Section */}
      <section id="waitlist" className="py-24 bg-gradient-to-br from-[#3498DB]/5 to-white">
        <WaitlistSection language={language} />
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <PricingTable language={language} />
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              {t.caseStudies.title}
            </h2>
            <p className="text-xl text-gray-600">
              {t.caseStudies.subtitle}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.caseStudies.cases.map((caseStudy, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 hover:border-[#3498DB] transition-all duration-300 hover:shadow-xl overflow-hidden hover-lift">
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#3498DB]/10 flex items-center justify-center">
                      {index === 0 && <Truck className="w-5 h-5 text-[#3498DB]" />}
                      {index === 1 && <Ambulance className="w-5 h-5 text-[#3498DB]" />}
                      {index === 2 && <Car className="w-5 h-5 text-[#3498DB]" />}
                    </div>
                    <span className="text-sm font-medium text-[#3498DB]">{caseStudy.type}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#2c3e50] mb-2">
                    {caseStudy.company}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {caseStudy.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t.caseStudies.readMore}</span>
                    <ChevronRight className="w-5 h-5 text-[#3498DB]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support/Contact Section */}
      <section id="support" className="py-24 bg-gradient-to-br from-[#3498DB]/5 via-white to-[#3498DB]/5 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#3498DB]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#3498DB]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Support Info */}
            <div>
              <div className="mb-12">
                <h2 className="text-4xl font-bold text-[#2c3e50] mb-6">
                  {t.support.title}
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  {t.support.subtitle}
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-[#3498DB]/10 rounded-full text-[#3498DB] font-medium">
                  <div className="w-2 h-2 bg-[#3498DB] rounded-full animate-pulse mr-2"></div>
                  {t.support.status}
                </div>
              </div>

              {/* Support Cards */}
              <div className="space-y-6">
                {/* Phone Support Card */}
                <div className="group bg-white p-6 rounded-xl border border-gray-100 hover:border-[#3498DB] transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center group-hover:bg-[#3498DB] group-hover:text-white transition-colors">
                      <MessageSquare className="h-6 w-6 text-[#3498DB] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#2c3e50] mb-1 group-hover:text-[#3498DB] transition-colors">
                        {t.support.cards.phone.title}
                      </h3>
                      <p className="text-gray-600">
                        {t.support.cards.phone.description}
                      </p>
                      <div className="mt-3 flex items-center text-[#3498DB]">
                        <span className="text-sm font-medium">{t.support.cards.phone.contact}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technical Support Card */}
                <div className="group bg-white p-6 rounded-xl border border-gray-100 hover:border-[#3498DB] transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center group-hover:bg-[#3498DB] group-hover:text-white transition-colors">
                      <Settings className="h-6 w-6 text-[#3498DB] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#2c3e50] mb-1 group-hover:text-[#3498DB] transition-colors">
                        {t.support.cards.technical.title}
                      </h3>
                      <p className="text-gray-600">
                        {t.support.cards.technical.description}
                      </p>
                      <div className="mt-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {t.support.cards.technical.response}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Management Card */}
                <div className="group bg-white p-6 rounded-xl border border-gray-100 hover:border-[#3498DB] transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-[#3498DB]/10 flex items-center justify-center group-hover:bg-[#3498DB] group-hover:text-white transition-colors">
                      <UserCheck className="h-6 w-6 text-[#3498DB] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#2c3e50] mb-1 group-hover:text-[#3498DB] transition-colors">
                        {t.support.cards.account.title}
                      </h3>
                      <p className="text-gray-600">
                        {t.support.cards.account.description}
                      </p>
                      <div className="mt-3 flex items-center space-x-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-[#3498DB]"></span>
                        <span className="text-sm text-gray-600">{t.support.cards.account.availability}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:sticky lg:top-24">
              <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-xl relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#3498DB]/20 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#3498DB]/10 to-transparent rounded-tr-full"></div>

                <div className="relative">
                  <h3 className="text-2xl font-semibold text-[#2c3e50] mb-2">
                    {t.support.form.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t.support.form.subtitle}
                  </p>

                  <ContactForm language={language} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#3498DB] to-[#2980B9] text-white gradient-animate relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 hover-scale">{t.cta.title}</h2>
            <p className="text-xl mb-8 text-blue-100">
              {t.cta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/coming-soon"
                className="bg-white text-[#2c3e50] px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition duration-300 shadow-lg hover:shadow-xl flex items-center justify-center hover-lift"
              >
                {t.cta.demo}
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/coming-soon"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition duration-300 flex items-center justify-center hover-lift"
              >
                {t.cta.contact}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 